import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../components/ui/Button';
export default function FAQ() {
  const [openItem, setOpenItem] = useState<string | null>('instructing');
  const toggleItem = (id: string) => {
    if (openItem === id) {
      setOpenItem(null);
    } else {
      setOpenItem(id);
    }
  };
  const faqCategories = [{
    id: 'instructing',
    title: 'Instructing Dr. Imtiaz Syed',
    items: [{
      question: 'How do I instruct Dr. Syed for a medico-legal report or assessment?',
      answer: 'The most efficient way to instruct Dr. Syed is to send an initial enquiry via email to enquiries@medicolegal-psychiatry.co.uk. Please provide a brief outline of the case, the type of assessment required, and any urgent deadlines. Our team will then guide you through the instruction process, including providing terms and conditions and a service agreement.'
    }, {
      question: 'What information is typically required to begin an assessment?',
      answer: 'To facilitate a thorough and timely assessment, we typically require: Letter of Instruction (clearly outlining questions to be addressed), Relevant medical records (GP notes, hospital records, psychiatric reports), Legal documents pertinent to the case (e.g., statements, court orders), Any previous expert reports, and Details for contacting the examinee. A comprehensive list will be provided upon initial enquiry.'
    }]
  }, {
    id: 'reports',
    title: 'Reports & Expert Witness Testimony',
    items: [{
      question: 'What is the typical turnaround time for a medico-legal report?',
      answer: "Dr. Syed is committed to responsive service. Our standard turnaround time for a comprehensive report is within 10 working days of the assessment. For urgent cases, expedited reports may be available within 5 working days, subject to Dr. Syed's availability and a potential surcharge. Please discuss any pressing deadlines with us at the outset."
    }, {
      question: 'Does Dr. Syed provide expert witness testimony in court or tribunals?',
      answer: 'Yes, Dr. Syed is highly experienced in providing robust and clear expert witness testimony in both court and tribunal settings, including Criminal, Family, Immigration, and Civil courts, as well as Mental Health Review Tribunals and Parole Board Hearings. His testimony is always impartial and grounded in his expert report findings.'
    }, {
      question: "What is Dr. Syed's approach to ensuring impartiality in his reports?",
      answer: "Dr. Syed adheres strictly to the expert's overriding duty to the court and the principles of the Civil Procedure Rules (CPR Part 35), Family Procedure Rules (FPR Part 25), and Criminal Procedure Rules (CrPR Part 19). All assessments are conducted objectively, and reports are evidence-based, impartial, and designed to assist the court, regardless of the instructing party."
    }]
  }, {
    id: 'assessments',
    title: 'Assessment Process & Accessibility',
    items: [{
      question: 'How are assessments conducted – remotely or in person?',
      answer: 'Dr. Syed offers excellent flexibility. Assessments can be conducted securely via remote video platforms (Zoom, Teams) across all regions of the UK. For in-person assessments, arrangements can be made in the West Midlands (Rugby base), East Midlands, and Greater London. Prison, care home visits, and court attendance are also available upon arrangement.'
    }, {
      question: 'Which mental health conditions does Dr. Syed assess?',
      answer: 'Dr. Syed assesses a comprehensive range of mental health conditions relevant to medico-legal contexts, including but not limited to: Major Depressive Disorder, Bipolar Affective Disorder, various Anxiety Disorders (GAD, Panic, Phobias), PTSD and Complex PTSD (including survivors of torture/modern slavery), Psychotic Disorders (e.g., Schizophrenia), OCD, ADHD, ASD, Personality Disorders, Substance Misuse, Neurocognitive Disorders (e.g., dementia), and more.'
    }, {
      question: 'Does Dr. Syed assess capacity for specific decisions (e.g., testamentary, DoLS, litigation)?',
      answer: 'Yes, Dr. Syed specialises in various mental capacity assessments under the Mental Capacity Act 2005, including capacity to litigate, testamentary capacity, capacity regarding property and affairs (COP3), capacity for residence and care decisions (including DoLS), and capacity for medical treatment decisions.'
    }]
  }, {
    id: 'fees',
    title: 'Fees & Billing',
    items: [{
      question: "How are Dr. Syed's fees structured?",
      answer: 'Fees for medico-legal assessments and expert witness services are typically based on the complexity of the case, the volume of documentation to be reviewed, the type of assessment required, and the estimated time involved. Detailed fee schedules and terms will be provided upon initial enquiry and prior to formal instruction. Please contact us for a tailored quotation.'
    }]
  }, {
    id: 'professional',
    title: 'Professional Standing',
    items: [{
      question: "What are Dr. Syed's professional qualifications and memberships?",
      answer: 'Dr. Syed holds an MBBS, MBA, and is currently pursuing an MSc in Clinical Psychiatry. He is a Member of the Royal College of Psychiatrists (MRCPsych), a Certified MedCo Medical Expert, and a Certified Expert Witness (Bond Solon and SEAK-trained). He is GMC-registered and maintains active memberships with professional bodies like the British Pakistani Psychiatrists Association (BPPA) and Expert Witness directories. Full details are available on the "About Dr. Imtiaz Syed" page.'
    }, {
      question: 'What languages does Dr. Syed speak?',
      answer: 'Dr. Syed is fluent in English and Urdu, and conversational in Hindi and Punjabi, which can facilitate communication during assessments where appropriate.'
    }]
  }];
  return <main className="min-h-screen bg-white" data-id="element-475">
      <Helmet data-id="element-476">
        <title data-id="element-477">Frequently Asked Questions – Medico-Legal Psychiatry</title>
        <meta name="description" content="Find answers to common questions about Dr. Imtiaz Syed's medico-legal psychiatric services, including report timelines, assessment formats, and professional qualifications." data-id="element-478" />
        <meta name="keywords" content="Medico-Legal Psychiatry FAQ, Expert Witness Psychiatrist Questions, Psychiatric Report Turnaround, Mental Capacity Assessment Process, GMC registered psychiatrist expert witness UK, MedCo accredited psychiatric expert witness, Independent PTSD assessment for asylum cases UK" data-id="element-479" />
        <script type="application/ld+json" data-id="element-480">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                ${faqCategories.flatMap(category => category.items.map(item => `
                      {
                        "@type": "Question",
                        "name": "${item.question}",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "${item.answer}"
                        }
                      }
                    `)).join(',')}
              ]
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 pt-24 md:pt-28" data-id="element-481">
        <div className="container mx-auto px-4" data-id="element-482">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-center" data-id="element-483">
            Frequently Asked Questions – Medico-Legal Psychiatry
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-484">
            Here you'll find answers to common questions regarding Dr. Imtiaz
            Syed's medico-legal psychiatric assessments and expert witness
            services.
          </p>
        </div>
      </section>
      {/* FAQ Content */}
      <section className="py-16" data-id="element-485">
        <div className="container mx-auto px-4" data-id="element-486">
          <div className="max-w-3xl mx-auto" data-id="element-487">
            <p className="text-slate-dark text-lg mb-8" data-id="element-488">
              Our aim is to provide transparent and helpful information to legal
              professionals, ensuring a smooth and efficient process.
            </p>
            <div className="space-y-6 mb-12" data-id="element-489">
              {faqCategories.map(category => <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-490">
                  <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openItem === category.id ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleItem(category.id)} data-id="element-491">
                    <h2 className="text-xl font-serif font-bold" data-id="element-492">
                      {category.title}
                    </h2>
                    {openItem === category.id ? <ChevronUp className="h-5 w-5" data-id="element-493" /> : <ChevronDown className="h-5 w-5" data-id="element-494" />}
                  </button>
                  {openItem === category.id && <div className="p-6 bg-white" data-id="element-495">
                      <div className="space-y-6" data-id="element-496">
                        {category.items.map((item, index) => <div key={index} data-id="element-497">
                            <h3 className="text-lg font-bold text-primary mb-2" data-id="element-498">
                              {item.question}
                            </h3>
                            <p className="text-slate-dark" data-id="element-499">{item.answer}</p>
                          </div>)}
                      </div>
                    </div>}
                </div>)}
            </div>
            {/* Cancellation Policy */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-500">
              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-501">
                Cancellation Policy
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-502">
                This policy is designed to ensure the efficient management of
                our professional commitments and to provide clear guidelines for
                all parties.
              </p>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-503">
                Standard Appointments and Assessments
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-504">
                Due to the significant time allocated for each assessment,
                including preparation and report writing, our cancellation
                policy is strictly enforced to maintain the quality of service
                for all clients.
              </p>
              <ul className="space-y-2 mb-4 text-slate-dark" data-id="element-505">
                <li className="flex items-start" data-id="element-506">
                  <span className="text-secondary font-bold mr-2" data-id="element-507">•</span>
                  <span data-id="element-508">
                    <strong data-id="element-509">More than 72 hours' notice:</strong> There will be
                    no charge for appointments cancelled with more than 72
                    hours' notice.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-510">
                  <span className="text-secondary font-bold mr-2" data-id="element-511">•</span>
                  <span data-id="element-512">
                    <strong data-id="element-513">Less than 72 hours' notice:</strong> The instructing
                    party will be charged for all preparation work already
                    undertaken at our Hourly Rate, plus any incurred travel time
                    and expenses.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-514">
                  <span className="text-secondary font-bold mr-2" data-id="element-515">•</span>
                  <span data-id="element-516">
                    <strong data-id="element-517">Less than 24 hours' notice or a "no-show":</strong>{' '}
                    The full fee for the scheduled assessment may be charged, as
                    this time cannot be reallocated to other clients.
                  </span>
                </li>
              </ul>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-518">
                Court and Tribunal Attendance
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-519">
                The cancellation of court or tribunal attendance with short
                notice can result in substantial costs and is subject to
                separate terms.
              </p>
              <ul className="space-y-2 mb-4 text-slate-dark" data-id="element-520">
                <li className="flex items-start" data-id="element-521">
                  <span className="text-secondary font-bold mr-2" data-id="element-522">•</span>
                  <span data-id="element-523">
                    <strong data-id="element-524">More than 7 working days' notice:</strong> There
                    will be no charge for the attendance fee. Any preparation
                    work already completed will be billed at the Hourly Rate.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-525">
                  <span className="text-secondary font-bold mr-2" data-id="element-526">•</span>
                  <span data-id="element-527">
                    <strong data-id="element-528">Less than 7 working days' notice:</strong> The full
                    fee for court attendance will be charged, in addition to all
                    preparation work, travel time, and expenses.
                  </span>
                </li>
              </ul>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-529">
                How to Cancel
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-530">
                All cancellations or requests to reschedule must be made in
                writing via email to enquiries@medicolegal-psychiatry.co.uk. The
                cancellation will be deemed effective from the time and date of
                receipt of the email.
              </p>
              <p className="text-slate-dark" data-id="element-531">
                We understand that unforeseen circumstances can arise and will
                consider exceptions on a case-by-case basis.
              </p>
            </div>
            <div className="text-center" data-id="element-532">
              <p className="text-slate-dark mb-4" data-id="element-533">
                Still have questions? We're here to help.
              </p>
              <Button variant="primary" size="lg" href="/contact" data-id="element-534">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}